import type { EditorExpressionFormValues } from "./schema-builder-field-editor-expression";
import type { EditorLabelFormValues } from "./schema-builder-field-editor-label";
import type { EditorNumberFormValues } from "./schema-builder-field-editor-number";
import type { EditorSelectFormValues } from "./schema-builder-field-editor-select";
import type { EditorTextareaFormValues } from "./schema-builder-field-editor-textarea";
import type {
  GetEditorValuesOptions,
  SchemaBuilderFieldEditorFormValues,
} from "./schema-builder-field-editor.type";
import type { SchemaField } from "@/entities/schema";

import { isSchemaFieldSeveritySection } from "@/entities/schema";

import { getSchemaBuilderFieldType } from "../schema-builder-field-type";

import {
  getEditorDefaultValues,
  getNewSchemaFieldOptionsFromValues,
  isEditorDefaultFormValuesEqual,
} from "./schema-builder-field-editor-default";
import {
  getEditorExpressionValues,
  getNewSchemaExpressionFromValues,
  isEditorExpressionFormValuesEqual,
} from "./schema-builder-field-editor-expression";
import {
  getEditorLabelValues,
  getNewSchemaLabelFromValues,
  isEditorLabelFormValuesEqual,
} from "./schema-builder-field-editor-label";
import {
  getEditorNumberValues,
  getNewSchemaNumberFromValues,
  isEditorNumberFormValuesEqual,
} from "./schema-builder-field-editor-number";
import {
  getEditorSelectValues,
  getNewSchemaSelectFromValues,
  isEditorSelectFormValuesEqual,
} from "./schema-builder-field-editor-select";
import {
  getEditorTextareaValues,
  getNewSchemaTextareaFromValues,
  isEditorTextareaFormValuesEqual,
} from "./schema-builder-field-editor-textarea";
import { SCHEMA_BUILDER_FIELD_EDIATBLE } from "./schema-builder-field-editor.constant";

export const isSchemaFieldEditable = (schema: SchemaField): boolean => {
  if (isSchemaFieldSeveritySection(schema)) return false;

  const fieldType = getSchemaBuilderFieldType(schema);

  return SCHEMA_BUILDER_FIELD_EDIATBLE[fieldType] ?? false;
};

export const getInitialValuesFromSchema = (schema: SchemaField) => {
  const initialFieldType = getSchemaBuilderFieldType(schema);

  const initialValues = generateFormValues({
    fieldType: initialFieldType,
    schema,
  });

  return {
    initialFieldType,
    initialValues,
  };
};

/**
 * It generates form values based on current `fieldType` and initial `schema`.
 * ⚠️ Pass `form` to also use existing form values depending on the context.
 */
export const generateFormValues = (
  options: GetEditorValuesOptions
): SchemaBuilderFieldEditorFormValues => {
  const { fieldType } = options;

  if (fieldType === "number") return getEditorNumberValues(options);

  if (fieldType === "select") return getEditorSelectValues(options);

  if (fieldType === "label") return getEditorLabelValues(options);

  if (fieldType === "textArea") return getEditorTextareaValues(options);

  if (fieldType === "expression") return getEditorExpressionValues(options);

  return {
    fieldType,
    ...getEditorDefaultValues(options),
  };
};

export const isFormValuesEqual = (
  a: SchemaBuilderFieldEditorFormValues,
  b: SchemaBuilderFieldEditorFormValues
) => {
  if (a.fieldType !== b.fieldType) return false;

  if (a.fieldType === "number")
    return isEditorNumberFormValuesEqual(a, b as EditorNumberFormValues);

  if (a.fieldType === "label")
    return isEditorLabelFormValuesEqual(a, b as EditorLabelFormValues);

  if (a.fieldType === "textArea")
    return isEditorTextareaFormValuesEqual(a, b as EditorTextareaFormValues);

  if (a.fieldType === "select")
    return isEditorSelectFormValuesEqual(a, b as EditorSelectFormValues);

  if (a.fieldType === "expression")
    return isEditorExpressionFormValuesEqual(
      a,
      b as EditorExpressionFormValues
    );

  return isEditorDefaultFormValuesEqual(a, b);
};

export const getNewSchemaFromValues = (
  values: SchemaBuilderFieldEditorFormValues
): SchemaField | null => {
  const { fieldType } = values;

  if (fieldType === "label") return getNewSchemaLabelFromValues(values);

  if (fieldType === "number") return getNewSchemaNumberFromValues(values);

  if (fieldType === "textArea") return getNewSchemaTextareaFromValues(values);

  if (fieldType === "select") return getNewSchemaSelectFromValues(values);

  if (fieldType === "expression")
    return getNewSchemaExpressionFromValues(values);

  const defaultValues = getNewSchemaFieldOptionsFromValues(values);

  if (fieldType === "text") {
    return { type: "input", inputType: "text", ...defaultValues };
  }

  if (fieldType === "checkbox" || fieldType === "assetUnlockToggler") {
    return { type: fieldType, ...defaultValues };
  }

  return null;
};
